"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Droplet } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SecureYourPosition() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const artists = [{
    name: "Marcus",
    style: "Wealth Strategy",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/new_black_male_suit.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/new_black_male_suit.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/new_black_male_suit.png-wvc-sizes",
    alt: "Portrait of a middle‑aged Black male with black and gray hair in a professional expensive suit",
    offset: "md:translate-x-0"
  }, {
    name: "Elena",
    style: "Credit Architecture",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/advisor_elena_portrait.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/advisor_elena_portrait.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/advisor_elena_portrait.webp-wvc-sizes",
    alt: "Portrait of Elena, Credit Architecture Specialist",
    offset: "md:translate-x-8"
  }, {
    name: "Julian",
    style: "Capital Funding",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/advisor_julian_portrait.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/advisor_julian_portrait.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/advisor_julian_portrait.png-wvc-sizes",
    alt: "Portrait of Julian, Funding Solutions Director",
    offset: "md:translate-x-16"
  }];
  return <section data-section-id="1378" id="secure-your-position" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      {}
      <Droplet className="absolute top-12 left-6 size-32 text-primary/[0.06] -rotate-12" strokeWidth={1.5} aria-hidden="true" />
      <Droplet className="absolute bottom-10 right-10 size-40 text-primary/[0.06] rotate-45" strokeWidth={1.5} aria-hidden="true" />

      {}
      <span aria-hidden="true" className="pointer-events-none absolute left-4 md:left-16 top-1/2 -translate-y-1/2 font-mono font-light text-primary/[0.04] leading-none select-none" style={{
      fontSize: "12vw"
    }}>
        § 06
      </span>

      <div ref={ref} className={`relative mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
          {}
          <div className="md:col-span-8">
            {}
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <span className="font-mono text-xs md:text-sm uppercase tracking-[0.2em] text-primary">
                § 06 — secure your position
              </span>
              <span className="h-px flex-1 max-w-24" style={{
              backgroundColor: "var(--primary)"
            }} aria-hidden="true" />
            </div>

            {}
            <h2 className="font-default font-medium text-foreground leading-[1.1]" style={{
            fontSize: "clamp(2rem, 4vw, 3.5rem)",
            letterSpacing: "-0.01em"
          }}>
              Ready to architect your <span className="font-serif italic font-normal text-primary">financial</span> future?
            </h2>

            {}
            <p className="mt-6 md:mt-8 max-w-xl text-foreground/80 font-default" style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.25rem)",
            lineHeight: 1.6
          }}>
              Our selective process ensures we only partner with serious individuals ready for elite wealth strategies.
            </p>

            {}
            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <Button asChild size="lg" className="rounded-md h-12 px-7 bg-primary text-primary-foreground hover:bg-primary/90 hover:-translate-y-0.5 transition-all duration-200 ease-out font-default font-semibold shadow-md">
                <Link to="/QualificationApplicationPage">
                  <Droplet className="size-4 fill-current" strokeWidth={1.5} />
                  Start Application
                </Link>
              </Button>

              <Link to="/ServicesPage" className="group inline-flex items-center gap-2 text-foreground font-default font-medium hover:text-primary transition-colors duration-200">
                <span>Explore our solutions</span>
                <ArrowRight className="size-4 transition-transform duration-200 ease-out group-hover:translate-x-1" strokeWidth={1.75} />
              </Link>
            </div>
          </div>

          {}
          <div className="md:col-span-4">
            <div className="flex md:flex-col gap-6 md:gap-5 justify-center md:justify-start overflow-x-auto md:overflow-visible -mx-6 px-6 md:mx-0 md:px-0">
              {artists.map((artist, i) => <div key={artist.name} data-index={i} className={`group flex-shrink-0 md:flex-shrink flex flex-col items-center md:items-start ${artist.offset}`}>
                  <div className="relative">
                    {}
                    <SafeHydrate><Avatar className="size-24 md:size-28 rounded-md overflow-hidden ring-1 ring-border group-hover:ring-primary transition-all duration-300 ease-out" style={{
                    borderRadius: "0rem"
                  }}>
                      <AvatarImage src={artist.src} data-wvc-srcset={artist.srcset} data-wvc-sizes={artist.sizes} alt={artist.alt} loading="lazy" decoding="async" className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] grayscale hover:grayscale-0" />
                      <AvatarFallback className="bg-secondary text-secondary-foreground font-default">
                        {artist.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>

                    {}
                    <span aria-hidden="true" className="absolute -top-1 -right-1 size-5 rounded-md flex items-center justify-center" style={{
                  backgroundColor: "var(--background)"
                }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="size-3.5 text-primary">
                        <path d="M6 19c0-7 5-13 13-13-1 7-6 13-13 13z" />
                        <path d="M6 19c2-4 5-7 9-9" />
                      </svg>
                    </span>
                  </div>

                  {}
                  <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-foreground/70 text-center md:text-left whitespace-nowrap">
                    {artist.name} · <span className="text-primary">{artist.style}</span>
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}